import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, InputNumber, Select, Space, Table, Typography } from 'antd';
import { DeleteOutlined, SaveOutlined, CalculatorOutlined } from '@ant-design/icons';
import { COMMISSIONS_CALCULATE_LABEL } from '../copy';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ListPage, FilterBar } from '../../../components/list';
import { toast } from '../../../lib/toast';
import { ApiError } from '../../../lib/api/error';
import { useContactOptions } from '../../../hooks/options/useContactOptions';
import { useCommissionPreview } from '../hooks/useCommissionPreview';
import { useSaveCommissions } from '../hooks/useSaveCommissions';
import {
  commissionAgentPreviewColumns,
  getCommissionLeadPreviewColumns,
} from '../commissionColumns';
import type { CommissionAgentPreview } from '../types';

dayjs.extend(utc);

const { Text } = Typography;
const { RangePicker } = DatePicker;

function recalculateAgentTotals(agent: CommissionAgentPreview): CommissionAgentPreview {
  const paidTotal = agent.lines.reduce((sum, line) => sum + line.paidAmount, 0);
  const originalTotal = agent.lines.reduce((sum, line) => sum + line.originalAmount, 0);
  return {
    ...agent,
    leadCount: agent.lines.length,
    originalTotal,
    paidTotal,
  };
}

const CommissionCalculatorPage: React.FC = () => {
  const [optionalAgentIds, setOptionalAgentIds] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [commissionRate, setCommissionRate] = useState<number | null>(null);
  const [previewAgents, setPreviewAgents] = useState<CommissionAgentPreview[]>([]);
  const [removedAgentIds, setRemovedAgentIds] = useState<Set<string>>(new Set());

  const { data: agents = [] } = useContactOptions({ role: 0 });

  const agentOptions = useMemo(
    () =>
      agents.map((c) => ({
        value: c.id,
        label: c.label,
      })),
    [agents],
  );

  const previewMutation = useCommissionPreview();
  const saveMutation = useSaveCommissions(() => {
    setOptionalAgentIds([]);
    setDateRange(null);
    setCommissionRate(null);
    setPreviewAgents([]);
    setRemovedAgentIds(new Set());
    previewMutation.reset();
  });

  const visibleAgents = useMemo(
    () => previewAgents.filter((a) => !removedAgentIds.has(a.contactId)),
    [previewAgents, removedAgentIds],
  );

  const canCalculate = dateRange !== null && commissionRate !== null;

  useEffect(() => {
    if (previewMutation.isError) {
      toast.error(ApiError.fromAxiosError(previewMutation.error).message);
    }
  }, [previewMutation.isError, previewMutation.error]);

  const handleReset = useCallback(() => {
    setOptionalAgentIds([]);
    setDateRange(null);
    setCommissionRate(null);
    setPreviewAgents([]);
    setRemovedAgentIds(new Set());
    previewMutation.reset();
  }, [previewMutation]);

  const handleCalculate = () => {
    if (!canCalculate || dateRange === null || commissionRate === null) {
      return;
    }

    const [from, to] = dateRange;
    previewMutation.mutate(
      {
        agentIds: optionalAgentIds,
        dateFrom: from.utc(true).startOf('day').toISOString(),
        dateTo: to.utc(true).endOf('day').toISOString(),
        commissionRate,
      },
      {
        onSuccess: (agents) => {
          setPreviewAgents(agents);
          setRemovedAgentIds(new Set());
          if (agents.length === 0) {
            toast.info('No qualifying agents found for the selected criteria.');
          }
        },
      },
    );
  };

  const handlePaidChange = (contactId: string, leadId: string, paid: number) => {
    setPreviewAgents((prev) =>
      prev.map((agent) => {
        if (agent.contactId !== contactId) {
          return agent;
        }

        const lines = agent.lines.map((line) =>
          line.leadId === leadId
            ? { ...line, paidAmount: Math.min(Math.max(0, paid), line.originalAmount) }
            : line,
        );

        return recalculateAgentTotals({ ...agent, lines });
      }),
    );
  };

  const handleSave = () => {
    const items = visibleAgents.flatMap((agent) =>
      agent.lines
        .filter((line) => line.paidAmount > 0)
        .map((line) => ({
          leadId: line.leadId,
          contactId: agent.contactId,
          commissionRate: line.commissionRate,
          originalCommissionAmount: line.originalAmount,
          commissionAmount: line.paidAmount,
          closingAmount: line.closingAmount,
          closingDate: line.closingDate,
        })),
    );

    if (items.length === 0) {
      toast.info('No commission lines with paid amount greater than zero.');
      return;
    }

    saveMutation.mutate({ items });
  };

  const totalPaid = useMemo(
    () => visibleAgents.reduce((sum, agent) => sum + agent.paidTotal, 0),
    [visibleAgents],
  );

  const handleRemoveAgent = useCallback((contactId: string) => {
    setRemovedAgentIds((prev) => new Set(prev).add(contactId));
  }, []);

  const agentColumns = useMemo(
    () => [
      ...commissionAgentPreviewColumns,
      {
        title: '',
        key: 'actions',
        width: 56,
        render: (_: unknown, record: CommissionAgentPreview) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            aria-label="Remove agent"
            onClick={() => handleRemoveAgent(record.contactId)}
          />
        ),
      },
    ],
    [handleRemoveAgent],
  );

  return (
    <ListPage
      title={COMMISSIONS_CALCULATE_LABEL}
      subtitle="Select closing date range and commission rate, then calculate agent payouts."
      actions={(
        <Space wrap>
          <Button
            type="primary"
            icon={<CalculatorOutlined />}
            onClick={handleCalculate}
            loading={previewMutation.isPending}
            disabled={!canCalculate}
          >
            Calculate
          </Button>
          {visibleAgents.length > 0 && (
            <Button
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saveMutation.isPending}
            >
              Disburse
            </Button>
          )}
        </Space>
      )}
      filters={(
        <FilterBar
          onReset={
            optionalAgentIds.length > 0 || dateRange !== null || commissionRate !== null
              ? handleReset
              : undefined
          }
        >
          <RangePicker
            value={dateRange}
            onChange={(dates) =>
              setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)
            }
          />
          <InputNumber<number>
            min={0.01}
            max={100}
            step={0.5}
            placeholder="Rate %"
            value={commissionRate}
            onChange={setCommissionRate}
            addonAfter="%"
            style={{ width: 140 }}
          />
          <Select<string[]>
            mode="multiple"
            allowClear
            placeholder="Filter agents (optional)"
            showSearch
            optionFilterProp="label"
            options={agentOptions}
            value={optionalAgentIds}
            onChange={setOptionalAgentIds}
            style={{ minWidth: 280 }}
          />
        </FilterBar>
      )}
    >
      <Table<CommissionAgentPreview>
        columns={agentColumns}
        dataSource={visibleAgents}
        loading={previewMutation.isPending}
        rowKey="contactId"
        bordered
        pagination={false}
        expandable={{
          expandedRowRender: (agent) => (
            <Table
              columns={getCommissionLeadPreviewColumns((leadId, paid) =>
                handlePaidChange(agent.contactId, leadId, paid),
              )}
              dataSource={agent.lines}
              rowKey="leadId"
              pagination={false}
              size="small"
            />
          ),
        }}
        locale={{
          emptyText: previewMutation.isSuccess
            ? 'No agents in this preview.'
            : 'Run calculate to preview agent commissions before disbursing.',
        }}
      />

      {visibleAgents.length > 0 && (
        <div className="cp-list-page-footer-total" style={{ textAlign: 'right', marginTop: 16 }}>
          <Text strong>
            Total paid ({visibleAgents.length} agents):{' '}
            {totalPaid.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </Text>
        </div>
      )}
    </ListPage>
  );
};

export default CommissionCalculatorPage;
